import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import AdminLayout from "../../components/admin/AdminLayout";
import { Check, Envelope, Phone, Storefront, User, ChatText } from "@phosphor-icons/react";

const STATUS_OPTIONS = [
  { value: "pending", label: "Pending", color: "bg-yellow-100 text-yellow-700" },
  { value: "contacted", label: "Contacted", color: "bg-blue-100 text-blue-700" },
  { value: "converted", label: "Converted", color: "bg-green-100 text-green-700" },
  { value: "ignored", label: "Ignored", color: "bg-gray-100 text-gray-600" },
];

export default function AdminLeads() {
  const [leads, setLeads] = useState([]);
  const [statusFilter, setStatusFilter] = useState("");
  const [loading, setLoading] = useState(true);

  const fetchLeads = async () => {
    setLoading(true);
    try {
      const qs = new URLSearchParams();
      if (statusFilter) qs.set("status", statusFilter);
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/leads?${qs}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("rs_token")}` },
      });
      if (!res.ok) throw new Error("Failed");
      setLeads(await res.json());
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchLeads(); }, [statusFilter]);

  async function updateStatus(leadId, newStatus) {
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/leads/${leadId}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("rs_token")}`,
        },
        body: JSON.stringify({ status: newStatus }),
      });
      if (!res.ok) throw new Error("Failed");
      toast.success("Lead updated");
      fetchLeads();
    } catch (e) {
      toast.error(e.message);
    }
  }

  return (
    <AdminLayout>
      <div className="space-y-4">
        <div className="flex items-center gap-3">
          <select className="input-pop" value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
            <option value="">All Statuses</option>
            {STATUS_OPTIONS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
          <div className="text-sm text-[var(--ink-muted)]">{leads.length} leads</div>
        </div>

        <div className="card-pop overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b-2" style={{ borderColor: "var(--border)" }}>
                <th className="py-2 px-3 text-left font-bold">Name</th>
                <th className="py-2 px-3 text-left font-bold">Business</th>
                <th className="py-2 px-3 text-left font-bold">Contact</th>
                <th className="py-2 px-3 text-left font-bold">Message</th>
                <th className="py-2 px-3 text-left font-bold">Status</th>
                <th className="py-2 px-3 text-left font-bold">Date</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={6} className="py-8 text-center text-[var(--ink-muted)]">Loading...</td></tr>}
              {!loading && leads.length === 0 && <tr><td colSpan={6} className="py-8 text-center text-[var(--ink-muted)]">No leads</td></tr>}
              {leads.map((l) => (
                <tr key={l.id} className="border-b hover:bg-gray-50" style={{ borderColor: "var(--border)" }}>
                  <td className="py-2 px-3">
                    <div className="flex items-center gap-1.5">
                      <User size={14} className="text-[var(--ink-muted)]" />
                      <span className="font-medium">{l.name}</span>
                    </div>
                  </td>
                  <td className="py-2 px-3">
                    <div className="flex items-center gap-1.5">
                      <Storefront size={14} className="text-[var(--ink-muted)]" />
                      <span>{l.business_name}</span>
                    </div>
                  </td>
                  <td className="py-2 px-3">
                    <div className="space-y-0.5">
                      <div className="flex items-center gap-1 text-xs">
                        <Envelope size={12} className="text-[var(--ink-muted)]" />
                        {l.email}
                      </div>
                      {l.phone && (
                        <div className="flex items-center gap-1 text-xs">
                          <Phone size={12} className="text-[var(--ink-muted)]" />
                          {l.phone}
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="py-2 px-3 text-xs max-w-xs truncate" title={l.message}>
                    {l.message || "-"}
                  </td>
                  <td className="py-2 px-3">
                    <select
                      className="text-xs font-bold px-2 py-1 rounded-full border-0 outline-none cursor-pointer"
                      style={{ background: "transparent" }}
                      value={l.status}
                      onChange={e => updateStatus(l.id, e.target.value)}
                    >
                      {STATUS_OPTIONS.map(s => (
                        <option key={s.value} value={s.value}>{s.label}</option>
                      ))}
                    </select>
                  </td>
                  <td className="py-2 px-3 text-xs text-[var(--ink-muted)]">
                    {new Date(l.created_at).toLocaleDateString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </AdminLayout>
  );
}